import { Box, Grid, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useBusinessProfiles } from '../hooks/useBusinessProfiles';
import { useUsage } from '../hooks/useCommon';
import { useCurrentPlan } from '../hooks/useCommon';
import { useLatestWeeklyIdea } from '../hooks/useContent';
import { useNavigate } from 'react-router-dom';
import PageSection from '../components/PageSection';
import { PageHeader, PageTitle, PageSubtitle } from '../components/PageSection';
import WelcomeSection from './dashboard/WelcomeSection';
import { BusinessProfilesCard, ContentStatsCard } from './dashboard/StatsCards';
import PlanStatusCard from './dashboard/PlanStatusCard';
import PlatformsCard from './dashboard/PlatformsCard';
import QuickTipsCard from './dashboard/QuickTipsCard';
import BusinessProfilesList from './dashboard/BusinessProfilesList';
import QuickActions from './dashboard/QuickActions';

// Dashboard container - matches PageContainer styling for consistency
// Note: This adds padding on top of MainLayout's padding, matching other pages that use PageSection
const DashboardContainer = styled(Box)(({ theme }: { theme: any }) => ({
  padding: theme.spacing(3),
  minHeight: '100vh',
  backgroundColor: '#F9FAFB', // colors.gray[50]
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

export default function Dashboard() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // console.log('🏠 Dashboard component mounted');
  // console.log('  - user:', user);
  
  // Use the query hooks
  const { data: businessProfiles, isLoading: loadingProfiles, error: profilesError } = useBusinessProfiles();
  const { data: usageData } = useUsage();
  const { data: currentPlan } = useCurrentPlan(user?.id);
  const { data: latestWeeklyIdeaData } = useLatestWeeklyIdea();
  // Use user data directly instead of separate API calls
  const emailStatus = { is_verified: user?.is_verified || true };
  const latestWeeklyIdea = latestWeeklyIdeaData?.idea ?? null;
  
  // console.log('📊 Dashboard data:');
  // console.log('  - businessProfiles:', businessProfiles);
  // console.log('  - loadingProfiles:', loadingProfiles);
  // console.log('  - profilesError:', profilesError);
  // console.log('  - emailStatus:', emailStatus);
  // console.log('  - user.is_verified:', user?.is_verified);
  // console.log('  - currentPlan:', currentPlan);
  
  // Fix: businessProfiles is already an array, not an object with profiles property
  const profiles = businessProfiles || [];
  const usage = usageData || {};
  
  // Debug profile structure
  if (profiles && profiles.length > 0) {
    // console.log('🔍 Profile structure debug:');
    // console.log('  - First profile:', profiles[0]);
    // console.log('  - Profile ID type:', typeof profiles[0].id);
    // console.log('  - Profile ID value:', profiles[0].id);
  }
  
  // console.log('📋 Processed profiles:', profiles);
  // console.log('📈 Usage data:', usage);

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleCreateProfile = () => {
    navigate('/business-profiles');
  };

  const handleEditProfile = (id: number) => {
    navigate(`/business-profiles/${id}/edit`);
  };

  const handleViewProfile = (id: number) => {
    navigate(`/business-profiles/${id}`);
  };

  const handleGenerateContent = (id: number) => {
    navigate(`/generate?profile=${id}`);
  };

  if (loadingProfiles) {
    return (
      <PageSection 
        title={t('dashboard:title')} 
        subtitle={t('dashboard:loading')}
        loading={true}
        loadingText={t('dashboard:loading')}
        hideTitleOnMobile
      >
        <div />
      </PageSection>
    );
  }

  if (profilesError) {
    return (
      <PageSection title={t('dashboard:title')} subtitle={t('common:error')} hideTitleOnMobile>
        <Alert severity="error">
          {t('dashboard:error')}
        </Alert>
      </PageSection>
    );
  }

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('common:greeting.morning');
    if (hour < 18) return t('common:greeting.afternoon');
    return t('common:greeting.evening');
  };

  return (
    <DashboardContainer>
      {/* Page Title - hidden on mobile (xs); header bar shows title there */}
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <PageHeader>
          <PageTitle>
            {t('dashboard:title')}
          </PageTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0 }}>
            <PageSubtitle sx={{ mb: 0 }}>
              {user?.first_name 
                ? `${getGreeting()}, ${user.first_name}!`
                : t('common:welcomeBack')}
            </PageSubtitle>
          {/* Role badge for admin/agency */}
          {(user?.role === 'admin' || user?.role === 'agency') && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: user?.role === 'admin' ? '#1976d2' : '#2e7d32',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {user?.role === 'admin' ? t('dashboard:roles.admin') : t('dashboard:roles.agency')}
            </Box>
          )}
        </Box>
      </PageHeader>
      </Box>

      {/* Quick Actions - Contextual guidance */}
      <Box sx={{ mb: 4 }}>
        <QuickActions 
          user={user} 
          profiles={profiles}
          usage={usage}
          currentPlan={currentPlan}
          emailStatus={emailStatus}
          latestWeeklyIdea={latestWeeklyIdea}
          onNavigate={handleNavigate} 
        />
      </Box>

      {/* Stats Cards Row - 3 equal columns: 2 stacked + 1 + 1 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Left Column - Stacked: Content Stats + Platforms (33%) */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Grid container spacing={3}>
            {/* Content Stats Card */}
            <Grid size={{ xs: 12 }}>
              <ContentStatsCard 
                totalCount={usage?.generations_used || (usage as any)?.content_generated || 0}
                monthCount={usage?.generations_used || (usage as any)?.content_generated || 0}
                monthLimit={currentPlan?.monthly_limit || 1000}
                onClick={() => handleNavigate('/history')}
              />
            </Grid>
            {/* Platforms Card */}
            <Grid size={{ xs: 12 }}>
              <PlatformsCard 
                currentPlan={currentPlan}
                onNavigate={handleNavigate}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Middle Column - Plan Status (33%) */}
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <PlanStatusCard 
            currentPlan={currentPlan}
            usage={usage}
            onNavigate={handleNavigate}
          />
        </Grid>

        {/* Right Column - Quick Tips (33%) */}
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <QuickTipsCard />
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={4}>
        {/* Left Side - Business Profile Section (Hidden for users with role 'client' or 'user') */}
        {user?.role !== 'client' && user?.role !== 'user' && (
          <Grid size={{ xs: 12, lg: 8 }}>
            <BusinessProfilesList
              profiles={profiles}
              onCreateProfile={handleCreateProfile}
              onEditProfile={handleEditProfile}
              onViewProfile={handleViewProfile}
              onGenerateContent={handleGenerateContent}
            />
          </Grid>
        )}

      </Grid>
    </DashboardContainer>
  );
}